import * as d3 from "d3";
import gradient from "./img/gradient.jpeg";
import styled from "styled-components/macro";
import RadarKey from "./RadarKey";
import { motion } from "framer-motion";
import { StyledTooltip } from "./StyledTooltip";

const width = 400;
const height = 800;
const size = 900;
const outterRadius = size / 2 - 100;
const innerCircleRadius = outterRadius / 2 + 20;
const radius = [270, innerCircleRadius - 80];
const dataPointCircleRadius = 24;
const imageSize = Math.sqrt(2) * dataPointCircleRadius;

const Wrapper = styled.div`
  display: inline-block;
  float: right;
  position: absolute;
  top: 0;
  right: 0;
  overflow: visible;
  @media screen and (max-width: 1000px) {
    display: none;
  }

  .gradient-bg {
    position: absolute;
    top: 0;
    right: 0;
    width: 500px;
    height: 100vh;
    object-fit: cover;
    z-index: 0;
  }

  svg {
    position: relative;
    z-index: 1;
    overflow: visible;
  }
`;

type CategoryRadarType = {
  data: {
    preferred: Array<{ name: string; link: string }>;
    learning: Array<{ name: string; link: string }>;
  };
};

// D3 arc generator
const createArc = d3.arc().padAngle(0);

// --- Framer Motion variants ---
const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.2, delayChildren: 0.4 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, scale: 0.9, y: 20 },
  show: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.39, 0.575, 0.565, 1],
    },
  },
};

const ringVariants = {
  hidden: { opacity: 0, scale: 0.8 },
  show: {
    opacity: 0.15,
    scale: 1,
    transition: {
      duration: 1,
      ease: "easeOut",
    },
  },
};

const gradientVariants = {
  hidden: { opacity: 0, y: 30 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 1.2,
      ease: "easeOut",
    },
  },
};

const SubRadar = ({ data }: CategoryRadarType) => {
  return (
    <Wrapper>
      <motion.img
        className="gradient-bg"
        // src={gradient}
        src={`${process.env.REACT_APP_S3_BASE_URL}gradient.jpeg`}
        alt="gradient background"
        variants={gradientVariants}
        initial="hidden"
        animate="show"
      />

      <RadarKey />

      <motion.svg
        width={400}
        height="100vh"
        variants={containerVariants}
        initial="hidden"
        animate="show"
      >
        <g transform={`translate(${width} ${height / 2 + 40})`}>
          {/* Main rings */}
          <motion.path
            variants={ringVariants}
            d={
              createArc({
                startAngle: Math.PI,
                endAngle: 2 * Math.PI,
                innerRadius: 0,
                outerRadius: outterRadius,
              })!
            }
            fill="#ffffff"
            stroke="lightgrey"
            opacity={0.1}
          />
          <motion.path
            variants={ringVariants}
            d={
              createArc({
                startAngle: Math.PI,
                endAngle: 2 * Math.PI,
                innerRadius: 0,
                outerRadius: innerCircleRadius,
              })!
            }
            fill="#ffffff"
            opacity={0.1}
          />

          {/* Data points (icons) */}
          {Object.entries(data).map(([group, icons], groupIdx) =>
            icons.map(({ name, link }, idx) => {
              const tooManyIcons = icons.length > 5;
              const spacingFactor = tooManyIcons ? 1.6 : 1.4;
              const angle =
                (2 * Math.PI) / (tooManyIcons ? 3.7 : 3) +
                spacingFactor *
                  ((Math.PI / (icons.length + 4)) * idx +
                    Math.PI / (4 * icons.length));

              return (
                <StyledTooltip
                  title={name}
                  aria-label={name}
                  placement="top"
                  arrow
                >
                  <motion.g
                    key={`${group}-${idx}`}
                    variants={itemVariants}
                    whileHover={{ scale: 1.1 }}
                  >
                    <circle
                      cx={radius[groupIdx] * Math.cos(angle)}
                      cy={radius[groupIdx] * Math.sin(angle)}
                      r={dataPointCircleRadius}
                      fill="white"
                    />
                    <image
                      x={radius[groupIdx] * Math.cos(angle) - imageSize / 2}
                      y={radius[groupIdx] * Math.sin(angle) - imageSize / 2}
                      href={link}
                      height={imageSize}
                      width={imageSize}
                    />
                  </motion.g>
                </StyledTooltip>
              );
            })
          )}
        </g>
      </motion.svg>
    </Wrapper>
  );
};

export default SubRadar;
